"use client"

import { FC, useRef } from "react"
import { Content } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import {
  LazyMotion,
  domAnimation,
  m,
  motion,
  useScroll,
  useTransform,
  useSpring,
  type Variants,
} from "motion/react"

export type HighlightedContentBoxProps =
  SliceComponentProps<Content.HighlightedContentBoxSlice>

const vFadeUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: d },
  }),
}

const vSlideLeft: Variants = {
  hidden: { opacity: 0, x: -32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  },
}

// Lotus-petal SVG — purely decorative
function LotosPetal({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 120 120" className={className} aria-hidden>
      {Array.from({ length: 8 }, (_, i) => {
        const angle = (i * 360) / 8
        return (
          <ellipse
            key={i}
            cx="60"
            cy="60"
            rx="10"
            ry="26"
            fill="currentColor"
            opacity="0.55"
            transform={`rotate(${angle} 60 60) translate(0 -20)`}
          />
        )
      })}
      <circle cx="60" cy="60" r="8" fill="currentColor" opacity="0.8" />
    </svg>
  )
}

const HighlightedContentBox: FC<HighlightedContentBoxProps> = ({ slice }) => {
  const { primary } = slice
  const sectionRef = useRef<HTMLElement>(null)

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  })

  // Parallax — lotus drifts upward
  const lotusY = useTransform(scrollYProgress, [0, 1], ["10%", "-20%"])
  const smoothLotusY = useSpring(lotusY, { stiffness: 50, damping: 18 })

  // Parallax — Om watermark drifts down slightly
  const omY = useTransform(scrollYProgress, [0, 1], ["-5%", "15%"])
  const smoothOmY = useSpring(omY, { stiffness: 45, damping: 20 })

  // Statement line grows from left as section scrolls in
  const lineScaleX = useTransform(scrollYProgress, [0.1, 0.5], [0, 1])

  return (
    <LazyMotion features={domAnimation}>
      <section
        ref={sectionRef}
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        aria-label={slice.slice_type
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}
        className="relative overflow-hidden bg-sacred-cream section-py"
      >
        {/* ── Background ornaments ── */}

        {/* Om watermark — drifts on scroll */}
        <m.div
          aria-hidden
          className="pointer-events-none absolute top-0 -right-8 text-[18rem] leading-none font-black select-none"
          style={{
            y: smoothOmY,
            background:
              "linear-gradient(160deg, var(--sacred-orange), var(--sacred-gold))",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            opacity: 0.06,
          }}
        >
          ॐ
        </m.div>

        {/* Lotus decoration — bottom left, drifts upward */}
        <m.div
          aria-hidden
          className="pointer-events-none absolute -bottom-16 -left-16 size-72 text-sacred-orange/[0.07]"
          style={{ y: smoothLotusY }}
        >
          <LotosPetal className="size-full" />
        </m.div>

        {/* Top chakra line */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(to right, transparent, var(--sacred-gold) 30%, var(--sacred-orange) 50%, var(--sacred-gold) 70%, transparent)",
          }}
        />
        {/* Bottom chakra line */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
          style={{
            background:
              "linear-gradient(to right, transparent, var(--sacred-gold) 30%, var(--sacred-orange) 50%, var(--sacred-gold) 70%, transparent)",
          }}
        />

        {/* ── Content ── */}
        <div className="relative z-10 container flex flex-col gap-10">
          {/* ── Title + Subtitle ── */}
          <m.div
            className="flex flex-col gap-3"
            variants={vFadeUp}
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            {primary.subtitle && (
              <div className="flex items-center gap-2">
                {/* Ornamental dot-bar before pill */}
                <span className="text-xs text-sacred-gold">✦</span>
                <span
                  className="rounded-full px-4 py-1 text-xs font-bold tracking-widest uppercase"
                  style={{
                    background:
                      "linear-gradient(90deg, oklch(0.94 0.06 55), oklch(0.97 0.04 72))",
                    color: "var(--sacred-maroon)",
                    boxShadow: "0 0 0 1px oklch(0.8 0.1 55 / 0.25)",
                  }}
                >
                  {primary.subtitle}
                </span>
                <span className="text-xs text-sacred-gold">✦</span>
              </div>
            )}

            {primary.title && (
              <div className="max-w-2xl">
                <PrismicRichText
                  field={primary.title}
                  components={{
                    heading1: ({ children }) => (
                      <h1 className="font-black text-sacred-red">{children}</h1>
                    ),
                    heading2: ({ children }) => (
                      <h2 className="font-black text-sacred-red">{children}</h2>
                    ),
                    heading3: ({ children }) => (
                      <h3 className="font-extrabold text-sacred-red">
                        {children}
                      </h3>
                    ),
                    paragraph: ({ children }) => (
                      <p className="text-2xl font-bold text-sacred-red">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
            )}

            {/* Animated underline that grows from left on scroll */}
            <m.div
              className="h-0.5 w-24 origin-left rounded-full"
              style={{
                scaleX: lineScaleX,
                background:
                  "linear-gradient(to right, var(--sacred-orange), var(--sacred-gold))",
              }}
            />
          </m.div>

          {/* ── Highlighted statement — big accent band ── */}
          {primary.highlighted_statement &&
            primary.highlighted_statement.length > 0 && (
              <m.div
                variants={vSlideLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="relative overflow-hidden rounded-2xl p-px"
                style={{
                  background:
                    "linear-gradient(120deg, var(--sacred-orange) 0%, var(--sacred-gold) 60%, var(--sacred-orange) 100%)",
                }}
              >
                {/* Inner fill */}
                <div
                  className="relative rounded-2xl px-8 py-7"
                  style={{
                    background:
                      "linear-gradient(135deg, oklch(0.975 0.025 72), oklch(0.955 0.04 68))",
                  }}
                >
                  {/* Glowing dot top-right */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute top-5 right-6 size-3 rounded-full"
                    style={{
                      background: "var(--sacred-gold)",
                      boxShadow: "0 0 12px 4px oklch(0.82 0.18 75 / 0.5)",
                    }}
                  />
                  {/* Left accent bar */}
                  <div
                    className="absolute inset-y-4 left-0 w-1 rounded-r-full"
                    style={{
                      background:
                        "linear-gradient(to bottom, var(--sacred-orange), var(--sacred-gold))",
                    }}
                  />
                  <div className="pl-5">
                    <PrismicRichText
                      field={primary.highlighted_statement}
                      components={{
                        heading2: ({ children }) => (
                          <h2 className="font-black text-sacred-orange">
                            {children}
                          </h2>
                        ),
                        heading3: ({ children }) => (
                          <h3 className="font-bold text-sacred-orange">
                            {children}
                          </h3>
                        ),
                        paragraph: ({ children }) => (
                          <p className="text-lg font-bold text-sacred-orange">
                            {children}
                          </p>
                        ),
                        strong: ({ children }) => (
                          <strong className="text-sacred-red">
                            {children}
                          </strong>
                        ),
                      }}
                    />
                  </div>
                </div>
              </m.div>
            )}

          {/* ── Quotes ── */}
          {primary.quotes && primary.quotes.length > 0 && (
            <div className="flex flex-col gap-4">
              {primary.quotes.map((item, i) => (
                <m.div
                  key={i}
                  variants={vFadeUp}
                  custom={i * 0.1}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  className="group relative overflow-hidden rounded-2xl"
                  style={{
                    background:
                      "linear-gradient(150deg, oklch(1 0.012 72 / 0.9), oklch(0.97 0.025 70))",
                    boxShadow:
                      "0 2px 24px oklch(0.6 0.14 42 / 0.07), inset 0 1px 0 oklch(1 0 0 / 0.65)",
                  }}
                >
                  {/* Top shimmer accent */}
                  <div
                    aria-hidden
                    className="absolute inset-x-0 top-0 h-px"
                    style={{
                      background:
                        "linear-gradient(to right, transparent, var(--sacred-gold) 35%, var(--sacred-orange) 65%, transparent)",
                    }}
                  />

                  {/* Hover shimmer sweep */}
                  <motion.div
                    className="pointer-events-none absolute inset-0 -translate-x-full"
                    style={{
                      background:
                        "linear-gradient(105deg, transparent 30%, oklch(0.97 0.04 72 / 0.5) 50%, transparent 70%)",
                    }}
                    whileHover={{ translateX: "100%" }}
                    transition={{ duration: 0.55, ease: "easeInOut" }}
                  />

                  <div className="relative flex items-start gap-4 px-6 py-5">
                    {/* Quotation mark glyph */}
                    <span
                      aria-hidden
                      className="mt-0.5 shrink-0 text-3xl leading-none font-black text-sacred-orange/30 select-none"
                    >
                      &ldquo;
                    </span>
                    <PrismicRichText
                      field={item.quote}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="text-base/relaxed text-foreground">
                            {children}
                          </p>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-bold text-sacred-red">
                            {children}
                          </strong>
                        ),
                        em: ({ children }) => (
                          <em className="text-muted-foreground italic">
                            {children}
                          </em>
                        ),
                      }}
                    />
                  </div>
                </m.div>
              ))}
            </div>
          )}

          {/* ── Footer text ── */}
          {primary.footer_text && primary.footer_text.length > 0 && (
            <m.div
              variants={vFadeUp}
              custom={0.15}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              className="flex items-start gap-3 border-t pt-6"
              style={{ borderColor: "oklch(0.82 0.1 55 / 0.3)" }}
            >
              {/* Small lotus marker */}
              <span
                aria-hidden
                className="mt-1 shrink-0 text-base leading-none text-sacred-gold/60 select-none"
              >
                ❋
              </span>
              <PrismicRichText
                field={primary.footer_text}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-sm/relaxed text-muted-foreground">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-bold text-foreground">
                      {children}
                    </strong>
                  ),
                }}
              />
            </m.div>
          )}
        </div>
      </section>
    </LazyMotion>
  )
}

export default HighlightedContentBox
